import type { ReactNode } from 'react';
import { failureKind, failureText, failureTitle } from '@/lib/errors';
import { Loading } from './ui';

type Q<T> = { data: T | undefined; isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown; isFetching?: boolean };

/** The error panel itself: what happened, and «Повторить». */
export function ErrorState({ error, onRetry, retrying, compact }: { error: unknown; onRetry: () => void; retrying?: boolean; compact?: boolean }) {
  const kind = failureKind(error);
  return (
    <div role="alert" className={compact ? 'rounded-lg border border-line px-4 py-5 text-sm' : 'rounded-lg border border-dashed border-strong px-6 py-10 text-center'}>
      <p className="font-medium">{failureTitle[kind]}</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted">{failureText[kind]}</p>
      {kind !== 'forbidden' && (
        <button type="button" className="btn-secondary mt-3" onClick={onRetry} disabled={retrying}>
          {retrying ? 'Повторяем…' : 'Повторить'}
        </button>
      )}
    </div>
  );
}

/**
 * One answer to «what do I show while this loads, and when it fails»: a skeleton, then
 * either the content or an error with «Повторить» — never a spinner that does not end.
 * A failed background refresh keeps the data already on screen.
 */
export function QueryState<T>({
  query,
  children,
  loading,
  compact,
}: {
  query: Q<T> | Q<unknown>[];
  children: ReactNode | ((data: T) => ReactNode);
  loading?: ReactNode;
  compact?: boolean;
}) {
  const qs = Array.isArray(query) ? query : [query];
  const failed = qs.find((q) => q.isError && q.data === undefined);
  if (failed) return <ErrorState error={failed.error} compact={compact} retrying={failed.isFetching} onRetry={() => qs.filter((q) => q.isError).forEach((q) => q.refetch())} />;
  if (qs.some((q) => q.isPending)) return <>{loading ?? <Loading />}</>;
  if (typeof children === 'function') return <>{children((qs[0] as Q<T>).data as T)}</>;
  return <>{children}</>;
}

/** For a screen that already branches on «no data yet»: the skeleton while it loads, the error with «Повторить» when it fails. */
export function QueryPending({ query, loading, compact }: { query: Q<unknown> | Q<unknown>[]; loading?: ReactNode; compact?: boolean }) {
  return (
    <QueryState query={query} loading={loading} compact={compact}>
      {null}
    </QueryState>
  );
}
